/**
 * Character detail page - displays character sheet with file folder tabs for pages.
 * Features a parchment-style character sheet with portrait, equipment, and completed adventures.
 */

import { Link, router } from '@inertiajs/react';
import { useState, useEffect, useCallback } from 'react';
import type { CharacterDetailPageProps, CharacterPage } from '@/types/characters';
import type { AdventureAttempt } from '@/types/adventures';
import type { Item } from '@/types/items';
import { Editor, EditorRenderer } from '@/components/Editor';
import { CharacterSheetFull } from '@/components/Characters/CharacterSheetFull';
import { LevelUpModal } from '@/components/Characters/LevelUpModal';
import { MediaUpload, MediaLibrary, ImageSizeSelector, type UploadedMedia, type ImageSize } from '@/components/Media';
import { ItemDetailModal, VaultItemCard } from '@/components/Vault';
import HexLayout, { type SidebarContext, type QuickLinksConfig, type BackLinkConfig } from '@/layouts/HexLayout';

/**
 * Get status stamp info for completed adventure envelope
 */
function getStatusStampInfo(attempt: AdventureAttempt): {
    text: string;
    color: string;
    borderColor: string;
    rotation: number;
} {
    const status = attempt.status.value;
    if (status === 'success') {
        return { text: 'SUCCESS', color: 'rgba(34, 197, 94, 0.8)', borderColor: 'rgba(22, 163, 74, 0.6)', rotation: -12 };
    }
    if (status === 'partial') {
        return { text: 'PARTIAL', color: 'rgba(234, 179, 8, 0.8)', borderColor: 'rgba(202, 138, 4, 0.6)', rotation: -6 };
    }
    if (status === 'failure') {
        return { text: 'FAILED', color: 'rgba(239, 68, 68, 0.8)', borderColor: 'rgba(220, 38, 38, 0.6)', rotation: -10 };
    }
    return { text: status.toUpperCase(), color: 'rgba(107, 114, 128, 0.8)', borderColor: 'rgba(75, 85, 99, 0.6)', rotation: -7 };
}

/**
 * Completed Adventure Envelope component
 */
function CompletedAdventureEnvelope({ attempt, hexId }: { attempt: AdventureAttempt; hexId: number }) {
    const statusStamp = getStatusStampInfo(attempt);
    const rotation = ((attempt.id * 11) % 17 - 8) * 0.3;

    return (
        <Link
            href={`/hex/${hexId}?tab=attempt-detail&attemptId=${attempt.id}`}
            className="block relative group"
            style={{ transform: `rotate(${rotation}deg)`, transition: 'transform 0.3s ease' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'rotate(0deg) translateY(-4px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = `rotate(${rotation}deg)`; }}
        >
            <div
                className="relative overflow-hidden rounded-sm"
                style={{
                    background: 'linear-gradient(180deg, #f5edd6 0%, #e8dcc4 30%, #d9cbb0 100%)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3), inset 0 -1px 0 rgba(0,0,0,0.1)',
                    border: '1px solid #c9b896',
                }}
            >
                {/* Envelope flap */}
                <div
                    className="absolute top-0 left-0 right-0 h-12 pointer-events-none"
                    style={{
                        background: 'linear-gradient(180deg, #ede4d0 0%, #e0d4be 100%)',
                        clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
                        borderBottom: '1px solid #c9b896',
                    }}
                />

                {/* Content */}
                <div className="pt-14 pb-4 px-6 relative">
                    <div className="text-center mb-2">
                        <div className="text-xs font-serif text-amber-800/50 uppercase tracking-widest mb-1">
                            Quest Report
                        </div>
                        <h3 className="text-lg font-serif font-bold text-amber-900 group-hover:text-amber-700 transition-colors">
                            {attempt.adventure?.name || 'Unknown Adventure'}
                        </h3>
                    </div>

                    <div className="flex items-center justify-center gap-4 text-sm font-serif text-amber-800/70">
                        <span>
                            {attempt.completed_at ? new Date(attempt.completed_at).toLocaleDateString() : 'N/A'}
                        </span>
                        {attempt.is_critical && (
                            <>
                                <span className="text-amber-800/30">|</span>
                                <span className="text-amber-600 font-semibold">Critical!</span>
                            </>
                        )}
                    </div>

                    {/* Status stamp */}
                    <div
                        className="absolute bottom-3 right-4"
                        style={{ transform: `rotate(${statusStamp.rotation}deg)` }}
                    >
                        <div
                            className="px-3 py-1 rounded-sm border-2 border-dashed"
                            style={{
                                borderColor: statusStamp.borderColor,
                                background: `linear-gradient(135deg, ${statusStamp.color.replace('0.8', '0.1')} 0%, ${statusStamp.color.replace('0.8', '0.15')} 100%)`,
                            }}
                        >
                            <div
                                className="text-xs font-serif font-bold uppercase tracking-wider"
                                style={{ color: statusStamp.color.replace('0.8', '1') }}
                            >
                                {statusStamp.text}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Link>
    );
}

export default function CharacterShow({
    character,
    pages,
    selectedPage,
    canEdit,
    hex,
    owner,
    statLabels,
    skillLabels,
    equipment,
    completedAttempts,
    isOwnerBlocked: initialIsOwnerBlocked = false,
    assignedBuilding,
}: CharacterDetailPageProps) {
    // Active tab: 'sheet' for character sheet, 'change-requests' for editing, or page ID for pages
    const [activeTab, setActiveTab] = useState<'sheet' | 'change-requests' | number>('sheet');
    const [showLevelUpModal, setShowLevelUpModal] = useState(false);
    // Change request form state
    const [editCharacterName, setEditCharacterName] = useState(character.name);
    const [editCharacterPronouns, setEditCharacterPronouns] = useState(character.pronouns || '');
    const [isSavingCharacter, setIsSavingCharacter] = useState(false);
    const [characterSaveError, setCharacterSaveError] = useState<string | null>(null);
    const [characterSaveSuccess, setCharacterSaveSuccess] = useState(false);
    const [isFormLifted, setIsFormLifted] = useState(false);
    const [formHasAnimated, setFormHasAnimated] = useState(false);
    const [showNewPageModal, setShowNewPageModal] = useState(false);
    const [newPageName, setNewPageName] = useState('');
    const [isCreatingPage, setIsCreatingPage] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editPageName, setEditPageName] = useState('');
    const [editPageContent, setEditPageContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [mediaList, setMediaList] = useState<UploadedMedia[]>([]);
    const [mediaLoading, setMediaLoading] = useState(false);
    const [pendingImageDrop, setPendingImageDrop] = useState<{ media: UploadedMedia; position: { x: number; y: number } } | null>(null);
    const [selectedEquipmentItem, setSelectedEquipmentItem] = useState<Item | null>(null);
    const [isOwnerBlocked, setIsOwnerBlocked] = useState(initialIsOwnerBlocked);
    const [isBlockingOwner, setIsBlockingOwner] = useState(false);

    // Get the currently displayed page
    const displayPage = activeTab === 'sheet' || activeTab === 'change-requests' ? null : pages.find(p => p.id === activeTab) || null;

    // Reset edit state when switching tabs
    useEffect(() => {
        if (displayPage) {
            setEditPageName(displayPage.name);
            setEditPageContent(displayPage.content || '');
        }
        setIsEditing(false);
        setSaveError(null);
    }, [activeTab, displayPage?.id]);

    // Load media when editing a page
    useEffect(() => {
        if (!isEditing || !displayPage) return;
        const fetchMedia = async () => {
            setMediaLoading(true);
            try {
                const response = await fetch(`/character/${character.id}/media?page_id=${displayPage.id}`, {
                    headers: { 'Accept': 'application/json' },
                });
                if (response.ok) {
                    const data = await response.json();
                    setMediaList(data.media || []);
                }
            } catch (err) {
                console.error('Failed to fetch media:', err);
            } finally {
                setMediaLoading(false);
            }
        };
        fetchMedia();
    }, [character.id, displayPage?.id, isEditing]);

    const handleCreatePage = (e: React.FormEvent) => {
        e.preventDefault();
        if (!newPageName.trim() || isCreatingPage) return;
        setIsCreatingPage(true);
        router.post(`/character/${character.id}/pages`, { name: newPageName.trim(), content: '' }, {
            onSuccess: () => { setShowNewPageModal(false); setNewPageName(''); },
            onFinish: () => setIsCreatingPage(false),
        });
    };

    const handleDeletePage = (pageId: number) => {
        if (!confirm('Are you sure you want to delete this page?')) return;
        router.delete(`/character/${character.id}/pages/${pageId}`, {
            onSuccess: () => {
                if (activeTab === pageId) {
                    setActiveTab('sheet');
                }
            },
        });
    };

    const handleSavePage = async () => {
        if (!displayPage || isSaving) return;
        setIsSaving(true);
        setSaveError(null);
        try {
            const csrfToken = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='))?.split('=')[1];
            const response = await fetch(`/character/${character.id}/pages/${displayPage.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-XSRF-TOKEN': csrfToken ? decodeURIComponent(csrfToken) : '',
                },
                body: JSON.stringify({ name: editPageName, content: editPageContent }),
                credentials: 'same-origin',
            });
            if (response.ok) {
                router.reload({ only: ['pages', 'selectedPage'] });
                setIsEditing(false);
            } else if (response.status === 422) {
                const data = await response.json();
                setSaveError(data.message || 'Validation error');
            } else {
                setSaveError('Failed to save page');
            }
        } catch {
            setSaveError('Failed to save page');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancelEdit = () => {
        if (displayPage) {
            setEditPageName(displayPage.name);
            setEditPageContent(displayPage.content || '');
        }
        setIsEditing(false);
        setSaveError(null);
    };

    // Handle saving character name/pronouns changes
    const handleSaveCharacter = async () => {
        if (isSavingCharacter) return;
        setIsSavingCharacter(true);
        setCharacterSaveError(null);
        setCharacterSaveSuccess(false);
        try {
            const csrfToken = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='))?.split('=')[1];
            const response = await fetch(`/character/${character.id}/update-details`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-XSRF-TOKEN': csrfToken ? decodeURIComponent(csrfToken) : '',
                },
                body: JSON.stringify({
                    name: editCharacterName.trim(),
                    pronouns: editCharacterPronouns.trim() || null,
                }),
                credentials: 'same-origin',
            });
            if (response.ok) {
                setCharacterSaveSuccess(true);
                router.reload({ only: ['character'] });
                // Lower the form and hide success message after 2 seconds
                setTimeout(() => {
                    setIsFormLifted(false);
                    setCharacterSaveSuccess(false);
                }, 2000);
            } else if (response.status === 422) {
                const data = await response.json();
                setCharacterSaveError(data.message || 'Validation error');
            } else {
                setCharacterSaveError('Failed to save changes');
            }
        } catch {
            setCharacterSaveError('Failed to save changes');
        } finally {
            setIsSavingCharacter(false);
        }
    };

    const handleMediaUpload = (media: UploadedMedia) => setMediaList(prev => [media, ...prev]);
    const handleMediaDelete = (mediaId: number) => setMediaList(prev => prev.filter(m => m.id !== mediaId));

    const handleImageDrop = useCallback((media: UploadedMedia, position: { x: number; y: number }) => {
        setPendingImageDrop({ media, position });
    }, []);

    const handleImageSizeSelect = useCallback((size: ImageSize, media: UploadedMedia) => {
        const url = media.urls[size];
        const altText = media.alt_text || media.original_filename;
        const markdown = `![${altText}](${url})`;
        setEditPageContent(prev => prev ? `${prev}\n\n${markdown}` : markdown);
        setPendingImageDrop(null);
    }, []);

    const handleToggleBlockOwner = useCallback(() => {
        if (isBlockingOwner || !owner.username) return;
        setIsBlockingOwner(true);
        if (isOwnerBlocked) {
            router.delete(`/missives/blocks/${owner.id}`, {
                preserveScroll: true,
                onSuccess: () => setIsOwnerBlocked(false),
                onFinish: () => setIsBlockingOwner(false),
            });
        } else {
            router.post('/missives/blocks', { blocked_username: owner.username }, {
                preserveScroll: true,
                onSuccess: () => setIsOwnerBlocked(true),
                onFinish: () => setIsBlockingOwner(false),
            });
        }
    }, [owner, isBlockingOwner, isOwnerBlocked]);

    const renderSidebar = ({ closeSidebar, isMobile }: SidebarContext) => (
        <>
            {/* Page Management - for owners */}
            {canEdit && (
                <div className="p-4 border-b border-slate-700">
                    <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                        Character Pages
                    </h2>
                    <button
                        onClick={() => setShowNewPageModal(true)}
                        className="w-full px-3 py-2 text-sm text-gray-300 hover:text-white bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                        <span>+</span>
                        <span>Add Page</span>
                    </button>
                    {pages.length > 0 && (
                        <div className="mt-3 space-y-1">
                            {pages.map((page) => (
                                <div
                                    key={page.id}
                                    className="group flex items-center justify-between text-sm"
                                >
                                    <span className="text-gray-400 truncate flex-1">{page.name}</span>
                                    {pages.length > 1 && (
                                        <button
                                            onClick={() => handleDeletePage(page.id)}
                                            className="px-2 py-1 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                            title="Delete page"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Owner section - for non-owners */}
            {!canEdit && (
                <div className="p-4 border-b border-slate-700">
                    <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                        Character Owner
                    </h2>
                    <div className="flex flex-col gap-2">
                        <span className="text-white font-medium">{owner.name}</span>
                        <div className="flex items-center gap-2">
                            {owner.username && !isOwnerBlocked && (
                                <Link
                                    href={`/missives/create?to=${encodeURIComponent(owner.username)}`}
                                    className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-1"
                                >
                                    Message
                                </Link>
                            )}
                            {owner.username && (
                                <button
                                    onClick={handleToggleBlockOwner}
                                    disabled={isBlockingOwner}
                                    className={`px-3 py-1.5 text-xs rounded-lg transition-colors flex items-center gap-1 ${
                                        isOwnerBlocked
                                            ? 'bg-green-600 hover:bg-green-700 text-white'
                                            : 'bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-600/50'
                                    } ${isBlockingOwner ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {isBlockingOwner ? '...' : isOwnerBlocked ? 'Unblock' : 'Block'}
                                </button>
                            )}
                        </div>
                        {isOwnerBlocked && (
                            <p className="text-xs text-red-400 mt-1">
                                You have blocked this user.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Assigned Building - shown when character is assigned to a building */}
            {assignedBuilding && (
                <div className="p-4 border-b border-slate-700">
                    <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                        Assigned To
                    </h2>
                    <div className="flex flex-col gap-1">
                        <Link
                            href={`/hex/${assignedBuilding.hex_id}/buildings/${assignedBuilding.id}`}
                            className="text-amber-400 hover:text-amber-300 transition-colors cursor-pointer"
                        >
                            {assignedBuilding.name || assignedBuilding.building_type.name}
                        </Link>
                        <span className="text-xs text-gray-500">
                            Slot {assignedBuilding.slot}
                        </span>
                    </div>
                </div>
            )}
        </>
    );

    // Configure Quick Links for the hex sidebar
    const quickLinksConfig: QuickLinksConfig = {
        hexId: hex.id,
        isOwner: canEdit,
        activeItem: 'characters',
    };

    // Configure back link to go to the roster (characters tab)
    const backLinkConfig: BackLinkConfig = {
        href: `/hex/${hex.id}?tab=characters`,
        label: 'Back to Roster',
    };

    return (
        <HexLayout
            title={`${character.name} - Character`}
            hex={hex}
            isOwner={canEdit}
            sidebar={renderSidebar}
            quickLinks={quickLinksConfig}
            backLink={backLinkConfig}
        >
            <div className="p-6">
                <div className="max-w-4xl mx-auto space-y-8">
                    {/* File Folder with Tabs */}
                    <div className="relative">
                        {/* Tab row */}
                        <div className="flex items-end gap-1 px-2">
                            {/* Character Sheet Tab */}
                            <button
                                onClick={() => setActiveTab('sheet')}
                                className={`relative px-4 py-2 font-serif font-semibold text-sm rounded-t-lg transition-all cursor-pointer ${
                                    activeTab === 'sheet'
                                        ? 'text-amber-950 z-10'
                                        : 'text-amber-900/70 hover:text-amber-900'
                                }`}
                                style={{
                                    background: activeTab === 'sheet'
                                        ? 'linear-gradient(180deg, #d4a574 0%, #c4956a 100%)'
                                        : 'linear-gradient(180deg, #a67c52 0%, #8b6542 100%)',
                                    borderTop: '1px solid #8b6542',
                                    borderLeft: '1px solid #8b6542',
                                    borderRight: '1px solid #8b6542',
                                    marginBottom: activeTab === 'sheet' ? '-1px' : '0',
                                    paddingBottom: activeTab === 'sheet' ? '9px' : '8px',
                                    boxShadow: activeTab === 'sheet' ? 'none' : 'inset 0 -2px 4px rgba(0,0,0,0.1)',
                                }}
                            >
                                📜 Character Sheet
                            </button>

                            {/* Page Tabs */}
                            {pages.map((page) => (
                                <button
                                    key={page.id}
                                    onClick={() => setActiveTab(page.id)}
                                    className={`relative px-4 py-2 font-serif font-semibold text-sm rounded-t-lg transition-all cursor-pointer ${
                                        activeTab === page.id
                                            ? 'text-amber-950 z-10'
                                            : 'text-amber-900/70 hover:text-amber-900'
                                    }`}
                                    style={{
                                        background: activeTab === page.id
                                            ? 'linear-gradient(180deg, #d4a574 0%, #c4956a 100%)'
                                            : 'linear-gradient(180deg, #a67c52 0%, #8b6542 100%)',
                                        borderTop: '1px solid #8b6542',
                                        borderLeft: '1px solid #8b6542',
                                        borderRight: '1px solid #8b6542',
                                        marginBottom: activeTab === page.id ? '-1px' : '0',
                                        paddingBottom: activeTab === page.id ? '9px' : '8px',
                                        boxShadow: activeTab === page.id ? 'none' : 'inset 0 -2px 4px rgba(0,0,0,0.1)',
                                    }}
                                >
                                    📖 {page.name}
                                </button>
                            ))}

                            {/* Change Requests Tab (for owners) */}
                            {canEdit && (
                                <button
                                    onClick={() => setActiveTab('change-requests')}
                                    className={`relative px-4 py-2 font-serif font-semibold text-sm rounded-t-lg transition-all cursor-pointer ${
                                        activeTab === 'change-requests'
                                            ? 'text-amber-950 z-10'
                                            : 'text-amber-900/70 hover:text-amber-900'
                                    }`}
                                    style={{
                                        background: activeTab === 'change-requests'
                                            ? 'linear-gradient(180deg, #d4a574 0%, #c4956a 100%)'
                                            : 'linear-gradient(180deg, #a67c52 0%, #8b6542 100%)',
                                        borderTop: '1px solid #8b6542',
                                        borderLeft: '1px solid #8b6542',
                                        borderRight: '1px solid #8b6542',
                                        marginBottom: activeTab === 'change-requests' ? '-1px' : '0',
                                        paddingBottom: activeTab === 'change-requests' ? '9px' : '8px',
                                        boxShadow: activeTab === 'change-requests' ? 'none' : 'inset 0 -2px 4px rgba(0,0,0,0.1)',
                                    }}
                                >
                                    Change Requests
                                </button>
                            )}

                            {/* Add Page Tab (for owners) */}
                            {canEdit && (
                                <button
                                    onClick={() => setShowNewPageModal(true)}
                                    className="px-3 py-2 font-serif text-sm rounded-t-lg text-amber-950/50 hover:text-amber-950/80 transition-colors cursor-pointer"
                                    style={{
                                        background: 'linear-gradient(180deg, #a67c52 0%, #8b6542 100%)',
                                        borderTop: '1px solid #8b6542',
                                        borderLeft: '1px solid #8b6542',
                                        borderRight: '1px solid #8b6542',
                                        boxShadow: 'inset 0 -2px 4px rgba(0,0,0,0.1)',
                                    }}
                                    title="Add new page"
                                >
                                    +
                                </button>
                            )}
                        </div>

                        {/* Tab Content - File Folder Body */}
                        <div
                            className="relative rounded-b-lg rounded-tr-lg overflow-hidden"
                            style={{
                                background: 'linear-gradient(135deg, #d4a574 0%, #c4956a 50%, #d4a574 100%)',
                                boxShadow: '4px 4px 12px rgba(0,0,0,0.3), -1px -1px 4px rgba(0,0,0,0.1)',
                                border: '1px solid #8b6542',
                            }}
                        >
                            {/* Aged edge effect */}
                            <div
                                className="absolute top-0 left-0 right-0 h-3 pointer-events-none"
                                style={{ background: 'linear-gradient(180deg, rgba(92,64,51,0.15) 0%, transparent 100%)' }}
                            />

                            {/* Content */}
                            <div className="p-6">
                                {activeTab === 'sheet' ? (
                                    /* Character Sheet Content */
                                    <div className="relative">
                                        {/* Paperclip decoration */}
                                        <div
                                            className="absolute -top-3 left-8 w-6 h-12 z-20 pointer-events-none"
                                            style={{
                                                background: 'linear-gradient(90deg, #a0a0a0 0%, #d0d0d0 30%, #a0a0a0 50%, #808080 100%)',
                                                borderRadius: '3px 3px 12px 12px',
                                                boxShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                                                transform: 'rotate(-5deg)',
                                            }}
                                        />
                                        <CharacterSheetFull
                                            character={character}
                                            canEdit={canEdit}
                                            onLevelUp={(character.can_level_up || character.has_pending_points) ? () => setShowLevelUpModal(true) : undefined}
                                        />
                                    </div>
                                ) : activeTab === 'change-requests' ? (
                                    /* Change Requests Content - Paper Form Aesthetic */
                                    <div className="max-w-2xl mx-auto py-4">
                                        {/* White paper form - lifts up when editing */}
                                        <div
                                            className={`relative p-8 ${formHasAnimated ? 'transition-all duration-300 ease-out' : ''}`}
                                            onClick={() => {
                                                if (!isFormLifted) {
                                                    setFormHasAnimated(true);
                                                    setIsFormLifted(true);
                                                }
                                            }}
                                            style={{
                                                background: 'linear-gradient(180deg, #ffffff 0%, #faf9f7 50%, #f5f3f0 100%)',
                                                boxShadow: isFormLifted
                                                    ? '0 20px 40px rgba(0,0,0,0.3), 0 10px 20px rgba(0,0,0,0.2), inset 0 0 30px rgba(0,0,0,0.03)'
                                                    : '4px 4px 12px rgba(0,0,0,0.2), inset 0 0 30px rgba(0,0,0,0.03)',
                                                border: '1px solid #e5e2dd',
                                                transform: isFormLifted ? 'scale(1) rotate(0deg)' : 'scale(0.92) rotate(-1.5deg)',
                                                cursor: isFormLifted ? 'default' : 'pointer',
                                            }}
                                        >
                                            {/* Paper texture overlay */}
                                            <div
                                                className="absolute inset-0 pointer-events-none opacity-20"
                                                style={{
                                                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23noise)' opacity='0.5'/%3E%3C/svg%3E")`,
                                                }}
                                            />

                                            {/* Header with rubber stamp look */}
                                            <div className="text-center mb-6">
                                                <div
                                                    className="inline-block px-6 py-2 border-2 border-dashed"
                                                    style={{
                                                        borderColor: 'rgba(180, 83, 9, 0.5)',
                                                        transform: 'rotate(-2deg)',
                                                    }}
                                                >
                                                    <h2
                                                        className="text-xl font-serif font-bold uppercase tracking-widest"
                                                        style={{ color: 'rgba(180, 83, 9, 0.7)' }}
                                                    >
                                                        Change Request Form
                                                    </h2>
                                                </div>
                                                <p className="mt-3 text-sm text-gray-500 font-serif italic">
                                                    Submit changes to your character's identity records
                                                </p>
                                            </div>

                                            {/* Form fields with lined paper effect */}
                                            <div className="space-y-6">
                                                {/* Character Name field */}
                                                <div>
                                                    <label className="block text-sm font-serif font-semibold text-gray-700 mb-2">
                                                        Character Name
                                                    </label>
                                                    <div
                                                        className="relative"
                                                        style={{
                                                            backgroundImage: 'repeating-linear-gradient(transparent 0px, transparent 31px, #d1d5db 31px, #d1d5db 32px)',
                                                            backgroundPosition: '0 0',
                                                        }}
                                                    >
                                                        <input
                                                            type="text"
                                                            value={editCharacterName}
                                                            onChange={(e) => setEditCharacterName(e.target.value)}
                                                            onFocus={() => { setFormHasAnimated(true); setIsFormLifted(true); }}
                                                            className="w-full px-2 py-1 bg-transparent border-none text-gray-900 font-serif text-lg focus:outline-none focus:ring-0 caret-gray-900"
                                                            style={{ lineHeight: '32px' }}
                                                            maxLength={100}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Pronouns field */}
                                                <div>
                                                    <label className="block text-sm font-serif font-semibold text-gray-700 mb-2">
                                                        Pronouns <span className="font-normal text-gray-400">(optional)</span>
                                                    </label>
                                                    <div
                                                        className="relative"
                                                        style={{
                                                            backgroundImage: 'repeating-linear-gradient(transparent 0px, transparent 31px, #d1d5db 31px, #d1d5db 32px)',
                                                            backgroundPosition: '0 0',
                                                        }}
                                                    >
                                                        <input
                                                            type="text"
                                                            value={editCharacterPronouns}
                                                            onChange={(e) => setEditCharacterPronouns(e.target.value)}
                                                            onFocus={() => { setFormHasAnimated(true); setIsFormLifted(true); }}
                                                            placeholder="subject/object"
                                                            className="w-full px-2 py-1 bg-transparent border-none text-gray-900 font-serif text-lg placeholder-gray-300 focus:outline-none focus:ring-0 caret-gray-900"
                                                            style={{ lineHeight: '32px' }}
                                                            maxLength={50}
                                                        />
                                                    </div>
                                                    <div className="mt-2 text-xs text-gray-500 font-serif space-y-1">
                                                        <p>
                                                            Format: <span className="font-semibold">subject/object</span> — used when describing actions in adventures.
                                                        </p>
                                                        {editCharacterPronouns.includes('/') ? (
                                                            <div className="text-gray-500 bg-gray-50 p-2 rounded border border-gray-200">
                                                                <span className="text-gray-400">Preview: </span>
                                                                "<em className="text-gray-700">{editCharacterPronouns.split('/')[0]}</em> rolled and got 42. This was a success since <em className="text-gray-700">{editCharacterPronouns.split('/')[0]}</em> needed ≤68%."
                                                            </div>
                                                        ) : (
                                                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-400">
                                                                <span><span className="text-gray-600">he/him</span> — "<em>he</em> rolled... <em>he</em> needed"</span>
                                                                <span><span className="text-gray-600">she/her</span> — "<em>she</em> rolled... <em>she</em> needed"</span>
                                                                <span><span className="text-gray-600">they/them</span> — "<em>they</em> rolled... <em>they</em> needed"</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Status messages */}
                                                {characterSaveError && (
                                                    <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm font-serif">
                                                        {characterSaveError}
                                                    </div>
                                                )}
                                                {characterSaveSuccess && (
                                                    <div className="p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm font-serif">
                                                        Changes saved successfully!
                                                    </div>
                                                )}

                                                {/* Submit section */}
                                                {(() => {
                                                    const hasChanges = editCharacterName.trim() !== character.name ||
                                                        editCharacterPronouns.trim() !== (character.pronouns || '');
                                                    return (
                                                        <div className="pt-6 border-t border-gray-200">
                                                            {/* Grandiose Signature */}
                                                            <div className="text-center mb-6">
                                                                <p className="text-xs text-gray-400 font-serif uppercase tracking-widest mb-1">
                                                                    Authorized by
                                                                </p>
                                                                <div
                                                                    className="text-4xl text-gray-700"
                                                                    style={{
                                                                        fontFamily: "'Sirivennela', cursive",
                                                                        textShadow: '1px 1px 0 rgba(0,0,0,0.1)',
                                                                        transform: 'rotate(-2deg)',
                                                                    }}
                                                                >
                                                                    {owner.name}
                                                                </div>
                                                                <div
                                                                    className="mt-1 mx-auto w-48 h-px"
                                                                    style={{
                                                                        background: 'linear-gradient(90deg, transparent 0%, #888 20%, #888 80%, transparent 100%)',
                                                                    }}
                                                                />
                                                            </div>

                                                            {/* Stamps row */}
                                                            <div className="flex items-center justify-center gap-6">
                                                                {/* Reject stamp - only show when lifted */}
                                                                {isFormLifted && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            // Reset to original values and lower
                                                                            setEditCharacterName(character.name);
                                                                            setEditCharacterPronouns(character.pronouns || '');
                                                                            setIsFormLifted(false);
                                                                        }}
                                                                        className="relative transition-all cursor-pointer hover:scale-105 active:scale-95"
                                                                        style={{
                                                                            transform: 'rotate(-4deg)',
                                                                        }}
                                                                    >
                                                                        <div
                                                                            className="px-5 py-2 border-2 border-dashed rounded-sm"
                                                                            style={{
                                                                                borderColor: 'rgba(220, 38, 38, 0.6)',
                                                                                background: 'rgba(220, 38, 38, 0.08)',
                                                                            }}
                                                                        >
                                                                            <span
                                                                                className="font-serif font-bold uppercase tracking-widest text-sm"
                                                                                style={{
                                                                                    color: 'rgba(220, 38, 38, 0.85)',
                                                                                    textShadow: '0 0 1px rgba(220, 38, 38, 0.3)',
                                                                                }}
                                                                            >
                                                                                Reject
                                                                            </span>
                                                                        </div>
                                                                    </button>
                                                                )}

                                                                {/* Approve/Approved stamp */}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (hasChanges) {
                                                                            handleSaveCharacter();
                                                                        } else {
                                                                            setIsFormLifted(false);
                                                                        }
                                                                    }}
                                                                    disabled={isSavingCharacter || !editCharacterName.trim()}
                                                                    className="relative transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
                                                                    style={{
                                                                        transform: 'rotate(3deg)',
                                                                    }}
                                                                >
                                                                    <div
                                                                        className="px-5 py-2 border-2 border-dashed rounded-sm"
                                                                        style={{
                                                                            borderColor: 'rgba(5, 150, 105, 0.6)',
                                                                            background: 'rgba(5, 150, 105, 0.08)',
                                                                        }}
                                                                    >
                                                                        <span
                                                                            className="font-serif font-bold uppercase tracking-widest text-sm"
                                                                            style={{
                                                                                color: 'rgba(5, 150, 105, 0.85)',
                                                                                textShadow: '0 0 1px rgba(5, 150, 105, 0.3)',
                                                                            }}
                                                                        >
                                                                            {isSavingCharacter ? 'Processing...' : hasChanges ? 'Approve' : 'Approved'}
                                                                        </span>
                                                                    </div>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>

                                            {/* Decorative paperclip */}
                                            <div
                                                className="absolute -top-2 right-8 w-4 h-10 z-20 pointer-events-none"
                                                style={{
                                                    background: 'linear-gradient(90deg, #a0a0a0 0%, #d0d0d0 30%, #a0a0a0 50%, #808080 100%)',
                                                    borderRadius: '2px 2px 8px 8px',
                                                    boxShadow: '1px 1px 3px rgba(0,0,0,0.3)',
                                                    transform: 'rotate(5deg)',
                                                }}
                                            />
                                        </div>
                                    </div>
                                ) : displayPage && (
                                    /* Page Content */
                                    <div>
                                        {isEditing ? (
                                            <div className="flex gap-6">
                                                <div className="flex-1">
                                                    <div className="mb-4">
                                                        <label className="block text-sm font-serif font-semibold text-amber-800 mb-1">Page Name</label>
                                                        <input
                                                            type="text"
                                                            value={editPageName}
                                                            onChange={(e) => setEditPageName(e.target.value)}
                                                            className="w-full max-w-md px-3 py-2 bg-white/50 border border-amber-900/30 rounded text-amber-900 font-serif focus:outline-none focus:ring-2 focus:ring-amber-600/50"
                                                        />
                                                    </div>
                                                    <div className="mb-4">
                                                        <label className="block text-sm font-serif font-semibold text-amber-800 mb-1">Content</label>
                                                        <div className="bg-slate-800 rounded-lg overflow-hidden">
                                                            <Editor value={editPageContent} onChange={setEditPageContent} placeholder="Write your page content..." height={400} onImageDrop={handleImageDrop} />
                                                        </div>
                                                    </div>
                                                    {saveError && <p className="mb-4 text-sm text-red-600">{saveError}</p>}
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            onClick={handleSavePage}
                                                            disabled={isSaving}
                                                            className="px-4 py-2 bg-green-700 hover:bg-green-800 disabled:bg-green-700/50 text-white rounded font-serif transition-colors"
                                                        >
                                                            {isSaving ? 'Saving...' : 'Save Page'}
                                                        </button>
                                                        <button
                                                            onClick={handleCancelEdit}
                                                            disabled={isSaving}
                                                            className="px-4 py-2 text-amber-800 hover:text-amber-900 font-serif transition-colors"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                                <aside className="w-64 flex-shrink-0">
                                                    <div className="bg-white/30 rounded-lg border border-amber-900/20 p-4 sticky top-0">
                                                        <h3 className="text-sm font-serif font-semibold text-amber-800 mb-4">Page Images</h3>
                                                        <MediaUpload uploadUrl={`/character/${character.id}/media`} pageId={displayPage.id} onUploadComplete={handleMediaUpload} onUploadError={(err) => console.error('Upload error:', err)} />
                                                        <div className="mt-4">
                                                            {mediaLoading ? (
                                                                <div className="text-center py-4 text-amber-800/50 text-sm font-serif">Loading images...</div>
                                                            ) : (
                                                                <MediaLibrary media={mediaList} deleteUrlBase={`/character/${character.id}/media`} onDelete={handleMediaDelete} canDelete={true} />
                                                            )}
                                                        </div>
                                                    </div>
                                                </aside>
                                            </div>
                                        ) : (
                                            /* Page View - Paper on folder */
                                            <div className="py-4">
                                                <div
                                                    className="relative p-8"
                                                    style={{
                                                        background: 'linear-gradient(180deg, #ffffff 0%, #faf9f7 50%, #f5f3f0 100%)',
                                                        boxShadow: '4px 4px 12px rgba(0,0,0,0.15), inset 0 0 30px rgba(0,0,0,0.03)',
                                                        border: '1px solid #e5e2dd',
                                                        transform: 'rotate(-0.3deg)',
                                                    }}
                                                >
                                                    {/* Paper texture overlay */}
                                                    <div
                                                        className="absolute inset-0 pointer-events-none opacity-20"
                                                        style={{
                                                            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23noise)' opacity='0.5'/%3E%3C/svg%3E")`,
                                                        }}
                                                    />

                                                    {/* Header */}
                                                    <div className="flex items-center justify-between mb-6 relative">
                                                        <h2 className="text-2xl font-serif font-bold text-gray-800">{displayPage.name}</h2>
                                                        {canEdit && (
                                                            <button
                                                                onClick={() => setIsEditing(true)}
                                                                className="px-3 py-1.5 text-sm font-serif text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                                                            >
                                                                Edit Page
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Content */}
                                                    <div className="relative">
                                                        {displayPage.content && displayPage.content.trim() !== '' ? (
                                                            <div className="max-w-none">
                                                                <EditorRenderer content={displayPage.content} theme="light" />
                                                            </div>
                                                        ) : (
                                                            <p className="text-gray-400 font-serif italic">
                                                                This page is empty.
                                                                {canEdit && (
                                                                    <button
                                                                        onClick={() => setIsEditing(true)}
                                                                        className="ml-2 text-gray-500 hover:text-gray-700 underline cursor-pointer"
                                                                    >
                                                                        Add content!
                                                                    </button>
                                                                )}
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Decorative paperclip */}
                                                    <div
                                                        className="absolute -top-2 right-12 w-4 h-10 z-20 pointer-events-none"
                                                        style={{
                                                            background: 'linear-gradient(90deg, #a0a0a0 0%, #d0d0d0 30%, #a0a0a0 50%, #808080 100%)',
                                                            borderRadius: '2px 2px 8px 8px',
                                                            boxShadow: '1px 1px 3px rgba(0,0,0,0.3)',
                                                            transform: 'rotate(8deg)',
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Equipment/Inventory Section - Wooden Chest */}
                    <div className="relative">
                        {/* Chest Container */}
                        <div
                            className="relative rounded-lg overflow-hidden"
                            style={{
                                background: 'linear-gradient(180deg, #5c3d2e 0%, #4a2f22 40%, #3d261b 100%)',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.1)',
                                border: '4px solid #2d1810',
                            }}
                        >
                            {/* Wood grain texture overlay */}
                            <div
                                className="absolute inset-0 pointer-events-none opacity-30"
                                style={{
                                    backgroundImage: `repeating-linear-gradient(
                                        90deg,
                                        transparent 0px,
                                        transparent 2px,
                                        rgba(0,0,0,0.1) 2px,
                                        rgba(0,0,0,0.1) 4px
                                    )`,
                                }}
                            />

                            {/* Chest lid/top edge */}
                            <div
                                className="h-8 relative"
                                style={{
                                    background: 'linear-gradient(180deg, #6b4432 0%, #5c3d2e 100%)',
                                    borderBottom: '3px solid #2d1810',
                                }}
                            >
                                {/* Metal band across top */}
                                <div
                                    className="absolute inset-x-4 top-1/2 -translate-y-1/2 h-3"
                                    style={{
                                        background: 'linear-gradient(180deg, #8b7355 0%, #6b5545 50%, #5a4535 100%)',
                                        borderRadius: '2px',
                                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), 0 1px 2px rgba(0,0,0,0.5)',
                                    }}
                                />
                                {/* Center clasp/lock */}
                                <div
                                    className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-sm"
                                    style={{
                                        background: 'linear-gradient(135deg, #c9a86c 0%, #a68b4b 50%, #8b7040 100%)',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.4)',
                                        border: '1px solid #6b5530',
                                    }}
                                >
                                    <div
                                        className="absolute inset-1 rounded-sm"
                                        style={{ background: 'radial-gradient(circle at 30% 30%, #d4b87a 0%, #a68b4b 100%)' }}
                                    />
                                </div>
                            </div>

                            {/* Title on chest */}
                            <div className="relative px-6 pt-2 pb-1">
                                <div className="flex items-center justify-center gap-2">
                                    <span className="text-lg">⚔️</span>
                                    <h2 className="text-lg font-serif font-bold text-amber-200/90 tracking-wider uppercase">
                                        Equipment
                                    </h2>
                                    <span className="text-sm font-normal text-amber-300/50">({equipment.length})</span>
                                </div>
                            </div>

                            {/* Chest interior */}
                            <div
                                className="relative mx-3 mb-3 rounded-sm p-4"
                                style={{
                                    background: 'linear-gradient(180deg, #2a1f18 0%, #1f1610 100%)',
                                    boxShadow: 'inset 0 4px 12px rgba(0,0,0,0.8)',
                                    border: '2px solid #1a110c',
                                }}
                            >
                                {equipment.length === 0 ? (
                                    <div className="text-center py-6">
                                        <p className="text-lg font-serif text-amber-200/50 italic mb-4">The chest is empty...</p>
                                        {canEdit && (
                                            <Link
                                                href={`/hex/${hex.id}?tab=vault`}
                                                className="inline-block px-4 py-2 rounded font-serif font-semibold transition-all hover:scale-105"
                                                style={{
                                                    background: 'linear-gradient(135deg, rgba(128, 90, 213, 0.9) 0%, rgba(107, 70, 193, 1) 100%)',
                                                    color: '#fff',
                                                    border: '1px solid rgba(128, 90, 213, 0.6)',
                                                }}
                                            >
                                                Go to Vault
                                            </Link>
                                        )}
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                        {equipment.map((item) => (
                                            <VaultItemCard
                                                key={item.id}
                                                item={item}
                                                onClick={() => setSelectedEquipmentItem(item)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Metal corners */}
                            {/* Top-left */}
                            <div
                                className="absolute top-0 left-0 w-8 h-8"
                                style={{
                                    background: 'linear-gradient(135deg, #8b7355 0%, #6b5545 100%)',
                                    clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                                    boxShadow: 'inset 1px 1px 0 rgba(255,255,255,0.3)',
                                }}
                            />
                            {/* Top-right */}
                            <div
                                className="absolute top-0 right-0 w-8 h-8"
                                style={{
                                    background: 'linear-gradient(225deg, #8b7355 0%, #6b5545 100%)',
                                    clipPath: 'polygon(0 0, 100% 0, 100% 100%)',
                                    boxShadow: 'inset -1px 1px 0 rgba(255,255,255,0.3)',
                                }}
                            />
                            {/* Bottom-left */}
                            <div
                                className="absolute bottom-0 left-0 w-8 h-8"
                                style={{
                                    background: 'linear-gradient(45deg, #8b7355 0%, #6b5545 100%)',
                                    clipPath: 'polygon(0 0, 0 100%, 100% 100%)',
                                    boxShadow: 'inset 1px -1px 0 rgba(255,255,255,0.2)',
                                }}
                            />
                            {/* Bottom-right */}
                            <div
                                className="absolute bottom-0 right-0 w-8 h-8"
                                style={{
                                    background: 'linear-gradient(315deg, #8b7355 0%, #6b5545 100%)',
                                    clipPath: 'polygon(100% 0, 0 100%, 100% 100%)',
                                    boxShadow: 'inset -1px -1px 0 rgba(255,255,255,0.2)',
                                }}
                            />
                        </div>
                    </div>

                    {/* Completed Adventures Section */}
                    {completedAttempts && completedAttempts.length > 0 && (
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-600/30 to-transparent" />
                                <h2 className="text-xl font-serif font-bold text-amber-100 tracking-wide flex items-center gap-2">
                                    <span>📜</span>
                                    <span>Completed Adventures</span>
                                    <span className="text-sm font-normal text-amber-200/60">({completedAttempts.length})</span>
                                </h2>
                                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-600/30 to-transparent" />
                            </div>

                            <div
                                className="rounded-lg p-6"
                                style={{
                                    background: 'linear-gradient(180deg, #5c4033 0%, #4a3429 50%, #3d2a1f 100%)',
                                    boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.5), 0 4px 20px rgba(0,0,0,0.3)',
                                    border: '4px solid #3d2a1f',
                                }}
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {completedAttempts.map((attempt) => (
                                        <CompletedAdventureEnvelope
                                            key={attempt.id}
                                            attempt={attempt}
                                            hexId={hex.id}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* New Page Modal */}
            {showNewPageModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div
                        className="w-full max-w-md rounded-lg p-6"
                        style={{
                            background: 'linear-gradient(135deg, #f5edd6 0%, #e8dcc4 50%, #f0e6d0 100%)',
                            boxShadow: '4px 4px 12px rgba(0,0,0,0.3)',
                            border: '1px solid #c9b896',
                        }}
                    >
                        <h3 className="text-lg font-serif font-bold text-amber-900 mb-4">Create New Page</h3>
                        <form onSubmit={handleCreatePage}>
                            <div className="mb-4">
                                <label className="block text-sm font-serif font-semibold text-amber-800 mb-2">Page Name</label>
                                <input
                                    type="text"
                                    value={newPageName}
                                    onChange={(e) => setNewPageName(e.target.value)}
                                    placeholder="e.g., Backstory, Notes..."
                                    className="w-full px-3 py-2 bg-white/50 border border-amber-900/30 rounded text-amber-900 font-serif placeholder-amber-800/40 focus:outline-none focus:ring-2 focus:ring-amber-600/50"
                                    autoFocus
                                />
                            </div>
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => { setShowNewPageModal(false); setNewPageName(''); }}
                                    className="px-4 py-2 font-serif text-amber-800 hover:text-amber-900 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!newPageName.trim() || isCreatingPage}
                                    className="px-4 py-2 font-serif font-semibold rounded transition-colors disabled:opacity-50"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(128, 90, 213, 0.9) 0%, rgba(107, 70, 193, 1) 100%)',
                                        color: '#fff',
                                    }}
                                >
                                    {isCreatingPage ? 'Creating...' : 'Create Page'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Level Up Modal */}
            {showLevelUpModal && (
                <LevelUpModal character={character} onClose={() => setShowLevelUpModal(false)} />
            )}

            {/* Image size selector */}
            {pendingImageDrop && (
                <ImageSizeSelector
                    media={pendingImageDrop.media}
                    position={pendingImageDrop.position}
                    onSelect={handleImageSizeSelect}
                    onCancel={() => setPendingImageDrop(null)}
                />
            )}

            {/* Equipment Item Detail Modal */}
            {selectedEquipmentItem && (
                <ItemDetailModal item={selectedEquipmentItem} characters={[]} onClose={() => setSelectedEquipmentItem(null)} showUnequip={true} showRecycle={true} />
            )}
        </HexLayout>
    );
}
